import React from "react";
import moment from 'moment';
import Icon from "../../jo-components/ui/icon/icon";
import { withErrors } from "../../jo-components/ui/inputErrors/inputErrors";
import {classnames} from "../../../utils/classnames";

export const CustomInputDate = ({
	id,
	onBlur,
	required = false,
	errors = null,
	onClick,
	inputRef,
	value,
	setValue,
	maxYear,
	minYear,
	maxMonth,
	minMonth,
	placeholder,
	dateFormat,
	formatWhenLosingFocus = () => {},
	getMomentDate,
	longDateFormat,
	ariaDescribedBy = '',
	ariaLabelAnnouncer = '',
	autoComplete = null,
	disabled = false
}) => {
	const [isFocused, setIsFocused] = React.useState(false);
	const useCharactersForRegex = (regexString) => [...regexString].map((char) => ['.', '-'].includes(char) ? `\\${char}` : char);

	const separatorCharacters = React.useMemo(() => {
		// get a list of all separator characters once
		if (!dateFormat?.length) return '';
		const isNum = (char) => char >= '0' && char <= '9';
		const isFirst = (char, index, string) => string.indexOf(char) === index;
		const individualCharacters = [...dateFormat].filter((char, index, string) => !isNum(char) && isFirst(char, index, string));
		return useCharactersForRegex(individualCharacters).join('');
	}, [dateFormat]);

	const getRegexFromFormat = React.useCallback((localeFormat, dateElements, lengthsSeparators) => {
		localeFormat = localeFormat.replace(new RegExp(`([${separatorCharacters}]+)`, 'gm'), '(?:$1)?')
		// replace element by regex group of same length
		dateElements.forEach((dateElement) => {
			const regexGroup = (length) => `(?<${dateElement}>[0-9]{0,${length}})`;
			localeFormat = localeFormat.replace(new RegExp(`${dateElement}+`, 'gm'), regexGroup(Math.max(2, lengthsSeparators[dateElement])));
		});
		// make the separators optional and in same group as date element
		const separatorOptional = new RegExp(`\\)\\(\\?:([${separatorCharacters}]+)\\)\\?`, 'gm');
		return localeFormat.replace(separatorOptional, (_, pattern) => '(?:' + useCharactersForRegex(pattern)
			.map((c, index) => `${c}{${index === 0 ? 1 : 0},1}`).join('') + ')?)');
	}, [separatorCharacters]);

	const capDateElement = React.useCallback((elementType, currentValues, defaultValue, lengthsElements) => {
		// limit Year/Month/Day value
		if (elementType === 'Y' && currentValues?.Y?.toString().length === lengthsElements['Y'] && (currentValues.Y < minYear || currentValues.Y > maxYear))
			return (currentValues.Y < minYear ? minYear : maxYear).toString();
		if (elementType === 'M') {
			if (minMonth !== null && currentValues.M < minMonth)
				return minMonth;
			if (currentValues.M > 12)
				return '12';
			if (maxMonth !== null && currentValues.M > maxMonth)
				return maxMonth;
		}
		const maxDay = new Date(currentValues.Y, currentValues.M, 0).getDate() || 31;
		if (elementType === 'D' && currentValues.D > maxDay)
			return maxDay.toString();
		return defaultValue;
	}, [maxYear, minYear, maxMonth, minMonth]);

	const autocompleteDateFormat = (dateFormatter, inputValue, separators, dateElements, previousValue, lengthsElements) => {
		dateFormatter.lastIndex = 0;
		// split the input into groups using the regex
		const matchFound = dateFormatter.exec(inputValue);
		const valuesDate = Object.fromEntries(Object.entries(matchFound.groups).map(([key, value]) => [key, parseInt(value)]));
		const stringsDate = matchFound?.splice(1, dateElements.length);
		return stringsDate?.reduce(
			(finalStr, dateElement, index) => {
				const elementSep = dateElement.replace(/[0-9]*/, '');
				let elementNum = dateElement.replace(/[^0-9]*/gm, '');
				elementNum = capDateElement(dateElements[index], valuesDate, elementNum, lengthsElements);

				// previous doesn't end with separator or parts of the separator (for multiple character separators)
				const prevElementHasNoSep = index > 0 && stringsDate[index - 1].replace(/[0-9]*/, '').length === 0;
				// 2 for empty separator first element + start index at 0
				const isLastIndex = index + 2 === separators.length;
				const addEndSeparator = !dateElement?.endsWith(separators[index + 1]) && !previousValue?.endsWith(separators[index + 1]);
				const lengthElement = `${valuesDate[dateElements[index]]}`?.length;
				// add separator only if missing
				if (dateElement.length)
					return `${
						finalStr
					}${
						prevElementHasNoSep ?
						separators[index] : ''
					}${
						elementNum + elementSep
					}${
						isLastIndex && addEndSeparator && lengthElement === lengthsElements[dateElements[index]] ? separators[index + 1] : ''
					}`;
				return finalStr;
			}, ''
		);
	};

	const handleAfterEndValue = (inputValue, dateFormatter, dateElementsOrder, lengthsElements) => {
		const matchesFound = [...inputValue.matchAll(dateFormatter)];
		if (matchesFound.length < 3) return inputValue;
		let groups = [...matchesFound[0]]?.splice(1, dateElementsOrder.length);
		const outsideValue = matchesFound[1][1];
		// add outside value to last element
		const lastGroup = groups[dateElementsOrder.length - 1];
		const sep = lastGroup?.replace(/[0-9]+/gm, '');
		const num = lastGroup?.replace(sep, '');
		if (num.length < lengthsElements[dateElementsOrder[dateElementsOrder.length - 1]])
			groups[dateElementsOrder.length - 1] = num + outsideValue + sep;
		return groups.join('');
	};

	const onChangeInput = React.useCallback(inputValue => {
		// get the format ex: DD-MM-YYYY
		let localeFormat = moment.localeData().longDateFormat(longDateFormat)
		let separators = localeFormat.split(new RegExp(`[^${separatorCharacters}]+`, 'gm'));
		const allSeparators = new RegExp(`[${separatorCharacters}]+`, 'gm')
		// get the elements order ['D', 'M', 'Y']
		const dateElementsOrder = localeFormat.split(allSeparators).map(char => char[0]).filter(val => val);
		const lengthsElements = dateElementsOrder.reduce(
			(lengths, dateElement) => {
				const lengthElement = localeFormat.match(new RegExp(dateElement, 'gm'))?.length || 0;
				lengths[dateElement] = lengthElement;
				return lengths;
			}, {});
		localeFormat = getRegexFromFormat(localeFormat, dateElementsOrder, lengthsElements);
		const dateFormatter = new RegExp(localeFormat, 'gm');
		// concat trailing value to last element
		inputValue = handleAfterEndValue(inputValue, dateFormatter, dateElementsOrder, lengthsElements);
		// check input validity and update
		const containsOnlyAllowed = new RegExp(`^[0-9${separatorCharacters}]*$`)
		if (containsOnlyAllowed.test(inputValue) && dateFormatter.test(inputValue)) {
			const formattedDate = autocompleteDateFormat(dateFormatter, inputValue, separators, dateElementsOrder, value, lengthsElements);
			setValue(formattedDate);
		}
	},
	[separatorCharacters, getMomentDate, setValue, value, longDateFormat]
	);

    return (
		<div
			tabIndex={-1}
			className={classnames("neo-custom-input", {
				"error": withErrors(errors),
				"disabled": disabled
			})}
			onClick={() => {
				if (!disabled) {
					onClick()
				}
			}}
		>
			<Icon
				tabIndex={-1}
				name="calendar"
				className="neo-custom-input__icon"
			/>
        	<input
				id={id}
				ref={inputRef}
				onFocus={() => setIsFocused(true)}
				onBlur={(event) => {setIsFocused(false); formatWhenLosingFocus(); onBlur(event)}}
				onKeyDown={(event) => {if (['Enter'].includes(event.key)) inputRef?.current?.blur()}}
				type="text"
				placeholder={!isFocused ? placeholder : ''}
				aria-describedby={ariaDescribedBy}
				aria-invalid={withErrors(errors)}
                aria-required={required}
				className={classnames("neo-custom-input__input", {
					"error": withErrors(errors),
					"disabled": disabled
				})}
				value={value}
				onChange={(e) => onChangeInput(e.target.value)}
				autoComplete={autoComplete}
				disabled={disabled}
			/>
			<div className="neo-date-div-aria-live" aria-live="assertive">{ariaLabelAnnouncer}</div>
		</div>
    );
};